import React, { useState, useEffect } from 'react';
import { Bell, X, UserPlus, Heart, MessageCircle, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { formatDistanceToNow } from 'date-fns';

interface NotificationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationsRead?: () => void;
}

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'friend_request' | 'friend_accepted';
  created_at: string;
  actor: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  post: {
    id: string;
    content: string;
  } | null;
  read: boolean;
}

interface FriendRequest {
  id: string;
  sender: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  created_at: string;
}

function NotificationsDialog({ isOpen, onClose, onNotificationsRead }: NotificationsDialogProps) {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<'notifications' | 'requests'>('notifications');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
      fetchFriendRequests();
    }
  }, [isOpen, user]);

  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id,
          type,
          created_at,
          read,
          actor:users!actor_id(id, username, display_name, avatar_url),
          post:posts(id, content)
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data || []);

      // Mark all notifications as read
      if (data && data.length > 0) {
        const { error: updateError } = await supabase
          .from('notifications')
          .update({ read: true })
          .eq('user_id', user?.id)
          .eq('read', false);

        if (!updateError && onNotificationsRead) {
          onNotificationsRead();
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('friend_requests')
        .select(`
          id,
          created_at,
          sender:users!friend_requests_sender_id_fkey(
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('receiver_id', user?.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFriendRequests(data || []);
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  };

  const handleFriendRequest = async (requestId: string, accept: boolean) => {
    if (!user || processingRequest) return;

    setProcessingRequest(true);
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({
          status: accept ? 'accepted' : 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      if (accept) {
        const request = friendRequests.find(r => r.id === requestId);
        if (request) {
          // Create notification for the sender
          await supabase
            .from('notifications')
            .insert({
              user_id: request.sender.id,
              type: 'friend_accepted',
              actor_id: user.id
            });
        }
      }

      // Refresh friend requests
      await fetchFriendRequests();
    } catch (error) {
      console.error('Error handling friend request:', error);
      alert('Failed to process friend request');
    } finally {
      setProcessingRequest(false);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like':
        return <Heart className="h-5 w-5 text-red-500" />;
      case 'comment':
        return <MessageCircle className="h-5 w-5 text-blue-500" />;
      case 'friend_request':
      case 'friend_accepted':
        return <UserPlus className="h-5 w-5 text-green-500" />;
    }
  };

  const getNotificationText = (notification: Notification) => {
    const actorName = notification.actor.display_name || notification.actor.username;
    
    switch (notification.type) {
      case 'like':
        return `${actorName} liked your post`;
      case 'comment':
        return `${actorName} commented on your post`;
      case 'friend_request':
        return `${actorName} sent you a friend request`;
      case 'friend_accepted':
        return `${actorName} accepted your friend request`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Notifications</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'notifications'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              Notifications
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'requests'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              Friend Requests
              {friendRequests.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {friendRequests.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'notifications' ? (
            loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No notifications yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  You'll see updates here when people interact with your posts
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 flex items-start space-x-3 ${
                      !notification.read ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <Link to={`/profile/${notification.actor.id}`}>
                      <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        {notification.actor.avatar_url ? (
                          <img
                            src={notification.actor.avatar_url}
                            alt={notification.actor.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </Link>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {getNotificationIcon(notification.type)}
                        <span className="font-medium">
                          {getNotificationText(notification)}
                        </span>
                      </div>
                      {notification.post && (
                        <Link
                          to="/"
                          className="text-sm text-gray-600 hover:text-gray-900 transition-colors line-clamp-2"
                        >
                          {notification.post.content}
                        </Link>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : friendRequests.length === 0 ? (
              <div className="text-center py-8">
                <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No friend requests</p>
                <p className="text-sm text-gray-500 mt-2">
                  When someone sends you a friend request, you'll see it here
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {friendRequests.map((request) => (
                  <div key={request.id} className="p-4 flex items-center space-x-4">
                    <Link to={`/profile/${request.sender.id}`}>
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        {request.sender.avatar_url ? (
                          <img
                            src={request.sender.avatar_url}
                            alt={request.sender.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/profile/${request.sender.id}`}
                        className="font-medium hover:text-indigo-600 transition-colors"
                      >
                        {request.sender.display_name || request.sender.username}
                      </Link>
                      <p className="text-sm text-gray-600">@{request.sender.username}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleFriendRequest(request.id, true)}
                        disabled={processingRequest}
                        className={`px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors ${
                          processingRequest ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleFriendRequest(request.id, false)}
                        disabled={processingRequest}
                        className={`px-3 py-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors ${
                          processingRequest ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationsDialog;